import { UserSelect } from "components/form/UserSelect";
import { Box, Button, Divider, Paragraph, Text, VStack } from "components/ui";
import { ChevronDown } from "lucide-react-native";
import React from "react";
import { UserGroup } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { CollapsableContainer } from "./CollapsableContainer";
import UserGroupCheckboxField from "./UserGroupCheckboxField";

const UserGroupForm = ({ userGroup, onChange, masterGroup }: { userGroup: UserGroup; onChange: (value: any, usergroupname: string | null | undefined) => void; masterGroup: boolean }) => {
	const [showOptions, setShowOptions] = React.useState(false);
	const isMobile = useIsMobile();

	const generalPermissions = [];

	if (masterGroup) {
		generalPermissions.push(
			{
				field: "canManageSites",
				label: "Manage Sites",
				value: userGroup?.canManageSites,
			},
			{
				field: "canViewReports",
				label: "View Reports",
				value: userGroup?.canViewReports,
			},
			{
				field: "canViewInvoices",
				label: "View Invoices",
				value: userGroup?.canViewInvoices,
			},
			{
				field: "allowOnlineOrdering",
				label: "Online Ordering",
				value: userGroup?.allowOnlineOrdering,
			},
			{
				field: "hidePrices",
				label: "Hide Prices",
				value: userGroup?.hidePrices,
			},
			{
				field: "enableRechargeable",
				label: "Enable Rechargeable",
				value: userGroup?.enableRechargeable,
				helpText: "Users will be able to track rechargables (spend that should be oncharged to a customer).",
			},
		);
	}

	const topLevelPermissions = [
		{
			field:"includeAllUsers",
			label: "Include All Users",
			value: userGroup?.includeAllUsers,
			helpText: "All users will be included in this group.",
		}
	];

	generalPermissions.push(
		{
			field: "canViewSiteBudget",
			label: "View Site Budget",
			value: userGroup?.canViewSiteBudget,
		},
		{
			field: "canViewEquipments",
			label: "View Equipment",
			value: userGroup?.canViewEquipments,
		},
	);

	const accountsPermissions = [
		{
			field: "accountSameAsAdmin",
			label: "Same as Administrator",
			value: true,
		},
	];

	const approvalPermissions = [
		{
			field: "ApprovalRequiredForAllOrders",
			label: "All Orders",
			value: userGroup?.approvalRequiredForAllOrders,
		},
		{
			field: "approvalRequiredForNonStockLists",
			label: "Non Stocklist Items",
			value: userGroup?.approvalRequiredForNonStockLists,
		},
		{
			field: "approvalRequiredForNonCatalogueItems",
			label: "Non Catalogue Items",
			value: userGroup?.approvalRequiredForNonCatalogueItems,
		},
		{
			field: "approvalRequiredForOrderOverSiteBudget",
			label: "Orders over site budget",
			value: userGroup?.approvalRequiredForOrderOverSiteBudget,
		},
		{
			field: "approvalRequiredForOrderOverGroupBudget",
			label: "Orders over group budget",
			value: userGroup?.approvalRequiredForOrderOverGroupBudget,
		},
		{
			field: "approvalRequiredForUrgentFastestShipping",
			label: "Orders on urgent/fastest shipping",
			value: userGroup?.approvalRequiredForUrgentFastestShipping,
		},
	];

	const stocklistPermissions = [
		{
			field: "limitViewingToStockListItems",
			label: "Limit viewing to stocklist items",
			value: userGroup?.limitViewingToStockListItems,
		},
		{
			field: "limitOrderingToStocklistItems",
			label: "Limit ordering to stocklist items",
			value: userGroup?.limitOrderingToStocklistItems,
		},
		{
			field: "limitOrderingToCatalogueItems",
			label: "Limit ordering to catalogue items",
			value: userGroup?.limitOrderingToCatalogueItems,
		},
	];

	const purchaseOrderPermissions = [
		{
			field: "purchaseOrderRequiredForAllOrders",
			label: "All Orders",
			value: userGroup?.purchaseOrderRequiredForAllOrders,
			helpText: "All orders will require a purchase order number.",
		},
		{
			field: "purchaseOrderRequiredForNonStocklistItems",
			label: "Non Stocklist Items",
			value: userGroup?.purchaseOrderRequiredForNonStocklistItems,
			helpText: "All non stocklist items will require a purchase order number.",
		},
		{
			field: "purchaseOrderRequiredForNonCatalogueItems",
			label: "Non Catalogue Items",
			value: userGroup?.purchaseOrderRequiredForNonCatalogueItems,
			helpText: "All non catalogue items will require a purchase order number.",
		},
	];

	function handleUserChange(value: any, usergroupname: string) {
		const newUserGroup = {
			...userGroup,
			userCollection: value,
		};

		onChange(newUserGroup, userGroup?.name);
	}

	function handlePermissionChange(value: boolean, field: keyof UserGroup) {
		const newUserGroup = {
			...userGroup,
			[field]: value,
		};

		onChange(newUserGroup, userGroup?.name);
	}

	return (
		<Box>
			<VStack position="relative" zIndex={9999}>
				<Box position="relative" zIndex={9} flexDirection="row" alignItems="center" gap={8}>
					<Paragraph fontWeight={"bold"}>{userGroup.name}</Paragraph>
				</Box>
				<Box position="relative" zIndex={99} my={12}>
					<UserSelect
						isFluid
						value={userGroup.userCollection ?? []}
						onSelect={(newValue) => {
							handleUserChange(newValue, userGroup.name ?? "");
						}}
					/>					
				</Box>
				<Box zIndex={10}  position="relative" justifyContent="flex-start" mt={-8}>
					{topLevelPermissions.map((permission, index) => (
						<UserGroupCheckboxField key={index} value={permission.value} field={permission.field as keyof UserGroup} label={permission.label} userGroup={userGroup} onChange={handlePermissionChange} helpText={permission.helpText} />
					))}
				</Box>
				<Box position="relative" zIndex={9} justifyContent="flex-start">
					<Button
						alignSelf="flex-start"
						paddingLeft={0}
						paddingTop={0}
						paddingBottom={0}
						onPress={() => {
							setShowOptions(!showOptions);
						}}
					>
						<Text fontSize={14}>Advanced</Text>
						<ChevronDown color="black" />
					</Button>
				</Box>
			</VStack>
			<Divider my={isMobile ? 24 : 28} bg="$neutral100" />

			<CollapsableContainer expanded={showOptions}>
				<Box flexDirection="row" alignItems="center" width={"$full"} gap={8}>
					<Paragraph>General</Paragraph>
				</Box>

				{generalPermissions.map((permission, index) => (
					<UserGroupCheckboxField key={index} value={permission.value} field={permission.field as keyof UserGroup} label={permission.label} userGroup={userGroup} onChange={handlePermissionChange} helpText={permission.helpText} />
				))}

				<Divider my={isMobile ? 24 : 28} bg="$neutral100" />
				<Box flexDirection="row" alignItems="center" gap={8}>
					<Paragraph>Approvers</Paragraph>
				</Box>
				<Box my={12}>
					<Text fontSize={14} fontWeight={"$bold"}>
						Require approval for the following:
					</Text>
				</Box>
				{approvalPermissions.map((permission, index) => (
					<UserGroupCheckboxField value={permission.value} key={index} field={permission.field as keyof UserGroup} label={permission.label} userGroup={userGroup} onChange={handlePermissionChange} helpText={permission.helpText} />
				))}
				<Divider my={isMobile ? 24 : 28} bg="$neutral100" />
				<Box flexDirection="row" alignItems="center" gap={8}>
					<Paragraph>Stocklist / Catalogue</Paragraph>
				</Box>
				{stocklistPermissions.map((permission, index) => (
					<UserGroupCheckboxField value={permission.value} key={index} field={permission.field as keyof UserGroup} label={permission.label} userGroup={userGroup} onChange={handlePermissionChange} helpText={permission.helpText} />
				))}
				<Divider my={isMobile ? 24 : 28} bg="$neutral100" />
				<Box flexDirection="row" alignItems="center" gap={8}>
					<Paragraph>Purchase Order</Paragraph>
				</Box>
				<Box my={12}>
					<Text fontSize={14} fontWeight={"$bold"}>
						Require purchase order number for the following
					</Text>
				</Box>
				{purchaseOrderPermissions.map((permission, index) => (
					<UserGroupCheckboxField value={permission.value} key={index} field={permission.field as keyof UserGroup} label={permission.label} userGroup={userGroup} onChange={handlePermissionChange} helpText={permission.helpText} />
				))}
				<Divider my={isMobile ? 24 : 28} bg="$neutral100" />
			</CollapsableContainer>
		</Box>
	);
};

export default UserGroupForm;
